// Adding someone by answering a few questions (brief 1D-5, mockup v50 Settings › Users and roles;
// PLAN.md 4.3; DECISIONS.md 10/24 no seat tiers). Pure: no database, no clock.
//
// - "What do they do?" is a job preset, as data: the areas and levels it opens, its switches, the
//   starting answer to "Which patients and clients?", and whether it's a field job.
// - The three yes or no questions turn a switch on or off: see pay and billing (see_money),
//   approve payroll (approve_payroll), release a held invoice or visit (release_block).
// - Fine-tune changes single areas and switches; what was changed there sticks over the preset
//   and the answers.
// - Billing counts office users and field users (free), worked out from what each person's roles
//   open: someone whose roles only open the field app is a field user; everyone else who has a
//   role is an office user. Nobody picks a tier.

import {
  AREAS,
  AREA_LABEL,
  LEVELS,
  SWITCH_AREA,
  SWITCH_LABEL,
  SWITCHES,
  type Access,
  type Area,
  type Level,
  type PermissionRow,
  type Switch,
} from "./permissions";

/** The answers to "Which patients and clients?" (mockup), as a preset's starting answer. */
export const SCOPE_ANSWERS = [
  "everything",
  "all_patients",
  "all_clients",
  "all_candidates",
  "counties",
  "clients",
  "teams",
  "own",
] as const;
export type ScopeAnswer = (typeof SCOPE_ANSWERS)[number];

export const SCOPE_ANSWER_LABEL: Record<ScopeAnswer, string> = {
  everything: "Everything",
  all_patients: "All patients",
  all_clients: "All clients",
  all_candidates: "All candidates",
  counties: "Certain counties",
  clients: "Certain clients",
  teams: "Certain teams or locations",
  own: "Only their own",
};

/** The kinds a scope row can be (user_scopes.kind). */
export const SCOPE_KINDS = ["client", "client_type", "county", "team", "location", "own"] as const;
export type ScopeKind = (typeof SCOPE_KINDS)[number];

/** Which scope kinds each answer is made of. "All ..." and Everything narrow nothing. */
export const ANSWER_KINDS: Record<ScopeAnswer, readonly ScopeKind[]> = {
  everything: [],
  all_patients: [],
  all_clients: [],
  all_candidates: [],
  counties: ["county"],
  clients: ["client", "client_type"],
  teams: ["team", "location"],
  own: ["own"],
};

/** The questions' switches, in the mockup's order. */
export const QUESTION_SWITCH = {
  money: "see_money",
  payroll: "approve_payroll",
  release: "release_block",
} as const satisfies Record<string, Switch>;
export type Question = keyof typeof QUESTION_SWITCH;
export const QUESTIONS = Object.keys(QUESTION_SWITCH) as Question[];

export const QUESTION_WORDS: Record<Question, string> = {
  money: "Should they see what people are paid and what is billed?",
  payroll: "Can they approve payroll?",
  release: "Can they release a held invoice or visit (with a reason)?",
};

/** A level as a preset or Fine-tune keeps it: "view", "edit", "view own", "edit own", or off. */
export type LevelSpec = "off" | "view" | "edit" | "view own" | "edit own";
const LEVEL_SPECS: readonly LevelSpec[] = ["off", "view", "edit", "view own", "edit own"];

export interface Preset {
  id: string;
  name: string;
  areas: Record<string, string>;
  switches: readonly string[];
  scope: ScopeAnswer;
  field: boolean;
}

export interface Answers {
  money: boolean;
  payroll: boolean;
  release: boolean;
  scope: ScopeAnswer;
}

/** Fine-tune: only what was changed there, over the preset and the answers. */
export interface FineTune {
  areas: Partial<Record<Area, LevelSpec>>;
  switches: Partial<Record<Switch, boolean>>;
}

export const NO_FINE: FineTune = { areas: {}, switches: {} };

const specOf = (v: unknown): LevelSpec =>
  LEVEL_SPECS.includes(v as LevelSpec) ? (v as LevelSpec) : "off";

/** A preset's starting answers: its switches say yes or no to each question. */
export function answersOf(p: Preset): Answers {
  return {
    money: p.switches.includes(QUESTION_SWITCH.money),
    payroll: p.switches.includes(QUESTION_SWITCH.payroll),
    release: p.switches.includes(QUESTION_SWITCH.release),
    scope: p.scope,
  };
}

/**
 * The role rows the preset, the answers and Fine-tune set: the preset's areas and switches, each
 * question's switch on or off as answered, then whatever Fine-tune changed. A field job has no
 * office switches. Every area gets a row (missing ones off), as roles keep them.
 */
export function rowsFor(p: Preset, a: Answers, fine: FineTune = NO_FINE): PermissionRow[] {
  const on = new Set<Switch>(p.switches.filter((s): s is Switch => s in SWITCH_AREA));
  if (!p.field)
    for (const q of QUESTIONS) {
      if (a[q]) on.add(QUESTION_SWITCH[q]);
      else on.delete(QUESTION_SWITCH[q]);
    }
  for (const [s, v] of Object.entries(fine.switches) as [Switch, boolean][]) {
    if (!(s in SWITCH_AREA)) continue;
    if (v) on.add(s);
    else on.delete(s);
  }
  return AREAS.map((area) => {
    const spec = fine.areas[area] ?? specOf(p.areas[area]);
    const level: Level = spec === "off" ? "none" : (spec.split(" ")[0] as Level);
    return {
      area,
      level,
      own_only: spec.endsWith(" own"),
      switches: Object.fromEntries(
        [...on].filter((s) => SWITCH_AREA[s] === area).map((s) => [s, true]),
      ),
    };
  });
}

/** Reads Fine-tune from a request: {areas: {patients: "view", ...}, switches: {see_money: true}}. */
export function readFineTune(
  v: unknown,
): { ok: true; value: FineTune } | { ok: false; error: string } {
  if (v === undefined || v === null) return { ok: true, value: NO_FINE };
  if (typeof v !== "object" || Array.isArray(v)) return { ok: false, error: "Fine-tune is a list" };
  const b = v as Record<string, unknown>;
  const areas: FineTune["areas"] = {};
  const switches: FineTune["switches"] = {};
  for (const [k, x] of Object.entries((b.areas ?? {}) as Record<string, unknown>)) {
    if (!(AREAS as readonly string[]).includes(k)) return { ok: false, error: `unknown area ${k}` };
    if (!LEVEL_SPECS.includes(x as LevelSpec))
      return { ok: false, error: `${AREA_LABEL[k as Area]} is Off, View or Edit` };
    areas[k as Area] = x as LevelSpec;
  }
  for (const [k, x] of Object.entries((b.switches ?? {}) as Record<string, unknown>)) {
    if (!(SWITCHES as readonly string[]).includes(k))
      return { ok: false, error: `unknown switch ${k}` };
    if (typeof x !== "boolean") return { ok: false, error: `${k} is yes or no` };
    switches[k as Switch] = x;
  }
  return { ok: true, value: { areas, switches } };
}

/** Reads the answers: three yes or no and a scope answer, the preset's for anything missing. */
export function readAnswers(
  v: unknown,
  p: Preset,
): { ok: true; value: Answers } | { ok: false; error: string } {
  const start = answersOf(p);
  if (v === undefined || v === null) return { ok: true, value: start };
  if (typeof v !== "object" || Array.isArray(v)) return { ok: false, error: "send the answers" };
  const b = v as Record<string, unknown>;
  const out = { ...start };
  for (const q of QUESTIONS) {
    if (b[q] === undefined) continue;
    if (typeof b[q] !== "boolean") return { ok: false, error: `${QUESTION_WORDS[q]} Yes or no.` };
    out[q] = b[q] as boolean;
  }
  if (b.scope !== undefined) {
    if (!SCOPE_ANSWERS.includes(b.scope as ScopeAnswer))
      return { ok: false, error: "Pick which patients and clients." };
    out.scope = b.scope as ScopeAnswer;
  }
  return { ok: true, value: out };
}

/** A preset made from rows (Something else): the rows' areas and switches, as data. */
export function presetFromRows(rows: readonly PermissionRow[]): Pick<Preset, "areas" | "switches"> {
  const areas: Record<string, string> = {};
  const switches: string[] = [];
  for (const r of rows) {
    if (r.level !== "none") areas[r.area] = `${r.level}${r.own_only ? " own" : ""}`;
    for (const [s, on] of Object.entries(r.switches)) if (on) switches.push(s);
  }
  return { areas, switches };
}

/** "This sets up: Patients: View · Visits: Edit · Sees pay, billing and margin". */
export function setsUp(
  rows: readonly PermissionRow[],
  field: boolean,
  shownAreas: readonly Area[] = AREAS,
): string {
  if (field) return "The phone app only: their own visits or trips, documents and chat.";
  const parts = rows
    .filter((r) => r.level !== "none" && shownAreas.includes(r.area))
    .map(
      (r) =>
        `${AREA_LABEL[r.area]}: ${r.level === "edit" ? "Edit" : "View"}${r.own_only ? " (own)" : ""}`,
    );
  const sw = rows
    .flatMap((r) => Object.entries(r.switches).filter(([, on]) => on))
    .map(([s]) => SWITCH_LABEL[s as Switch]);
  const out = [...parts, ...sw];
  return out.length ? out.join(" · ") : "Nothing yet";
}

/** The areas the field apps open, only their own: the caregiver's visits and patients, the
 * driver's trips (and the fleet they drive, to view). */
const FIELD_OWN: readonly Area[] = ["patients", "visits", "trips", "workers"];

/**
 * A field user: someone whose roles only open the field app (only their own visits, patients or
 * trips, and at most a view of the fleet), with no switch. Everyone else with any role is an
 * office user. Someone with no role at all is neither.
 */
export function userKind(access: Access): "office" | "field" | "none" {
  if (access.admin) return "office";
  let any = false;
  for (const area of AREAS) {
    const a = access.areas[area];
    if (a.view === "none" && a.edit === "none") continue;
    any = true;
    if (area === "fleet" && a.edit === "none") continue;
    if (!FIELD_OWN.includes(area)) return "office";
    if (a.view === "all" || a.edit === "all") return "office";
  }
  if (SWITCHES.some((s) => access.switches[s])) return "office";
  return any ? "field" : "none";
}

/** Office users and field users (free), counted from what each person's roles open. */
export function userCounts(kinds: readonly ("office" | "field" | "none")[]): {
  office: number;
  field: number;
} {
  return {
    office: kinds.filter((k) => k === "office").length,
    field: kinds.filter((k) => k === "field").length,
  };
}

/** The Fine-tune grid's levels for a set of rows (Off, View, Edit; own kept). */
export function gridOf(rows: readonly PermissionRow[]): Record<Area, LevelSpec> {
  return Object.fromEntries(
    AREAS.map((a) => {
      const r = rows.find((x) => x.area === a);
      const spec: LevelSpec =
        !r || r.level === "none" ? "off" : (`${r.level}${r.own_only ? " own" : ""}` as LevelSpec);
      return [a, spec];
    }),
  ) as Record<Area, LevelSpec>;
}

export const levelOk = (l: string): l is Level => (LEVELS as readonly string[]).includes(l);

/** "Today", "Yesterday", the weekday this week, else MM/DD (mockup's Last active). */
export function lastActiveWords(day: string | null, today: string): string {
  if (!day) return "–";
  const ms = Date.parse(`${today}T12:00:00Z`) - Date.parse(`${day}T12:00:00Z`);
  const days = Math.round(ms / 86_400_000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7)
    return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
      new Date(`${day}T12:00:00Z`).getUTCDay()
    ] as string;
  return `${day.slice(5, 7)}/${day.slice(8, 10)}`;
}

/** A job name typed under "Something else": 1 to 40 characters, not one already there. */
export function readJobName(
  v: unknown,
  taken: readonly string[],
): { ok: true; value: string } | { ok: false; error: string } {
  const name = typeof v === "string" ? v.replace(/\s+/g, " ").trim() : "";
  if (!name || name.length > 40) return { ok: false, error: "Name the job, up to 40 characters." };
  if (taken.some((t) => t.toLowerCase() === name.toLowerCase()) || name.toLowerCase() === "admin")
    return { ok: false, error: `There's already a job called ${name}.` };
  return { ok: true, value: name };
}

/**
 * The areas a company's menus open (everything follows what the company has): the areas the
 * screens of its sections need, for the modules it has on, in the usual order. Fine-tune and
 * "This sets up" show only these.
 */
export function areasFor(
  sections: readonly {
    module?: string;
    screens: readonly { need?: unknown; module?: string }[];
  }[],
  on: readonly string[],
  /** The areas the modules that are on bring beyond their screens (their manifests, brief 1E-8). */
  brought: readonly Area[] = [],
): Area[] {
  const used = new Set<Area>(brought);
  for (const s of sections) {
    if (s.module && !on.includes(s.module)) continue;
    for (const x of s.screens) {
      if (x.module && !on.includes(x.module)) continue;
      const need = x.need as { area?: Area } | undefined;
      if (need?.area) used.add(need.area);
    }
  }
  return AREAS.filter((a) => used.has(a));
}

/** The answers to "Which patients and clients?" a company has: no candidates without Staffing,
 * no teams or locations until it has some, no patients without Home Care. */
export function scopeAnswersFor(on: readonly string[], hasTeams = false): ScopeAnswer[] {
  return SCOPE_ANSWERS.filter((a) => {
    if (a === "all_candidates") return on.includes("staffing");
    if (a === "all_patients" || a === "counties") return on.includes("homecare");
    if (a === "teams") return hasTeams;
    return true;
  });
}
